/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/yard/YardDashboard.tsx
 * ----------------------------------------------------------------------------
 * Painel principal do módulo YMS (Yard Management System).
 *
 * Primeira versão:
 *  - Usa dados "mock" em memória (sem chamar API ainda).
 *  - Mostra contadores simples (viaturas, docas, janelas).
 *  - Mostra tabelas resumidas de docas e viaturas.
 *
 * Depois, quando criarmos os endpoints DRF /api/yms/...,
 * basta substituir os arrays mock por chamadas fetch/axios.
 * ========================================================================== */

import React, { useMemo } from "react";
import { Truck, Warehouse, Clock } from "lucide-react";

// ---------------------------------------------------------------------------
// Tipos de dados (alinhados com o que temos no backend/yard/models.py)
// ---------------------------------------------------------------------------

type DockStatus = "FREE" | "RESERVED" | "IN_OPERATION" | "BLOCKED" | "MAINTENANCE";
type DockKind = "INBOUND" | "OUTBOUND" | "MIXED";

type VehicleStatus =
    | "PLANNED"
    | "ARRIVED"
    | "CHECKED_IN"
    | "WAITING"
    | "CALLED_TO_DOCK"
    | "AT_DOCK"
    | "COMPLETED"
    | "CHECKED_OUT";

type OperationType = "INBOUND" | "OUTBOUND" | "RETURN" | "CROSS_DOCKING";
type SlotStatus = "PLANNED" | "CONFIRMED" | "IN_EXECUTION" | "COMPLETED" | "NO_SHOW" | "CANCELLED";

interface YardDock {
    id: number;
    warehouseCode: string;
    code: string;
    kind: DockKind;
    status: DockStatus;
}

interface YardVehicle {
    id: number;
    licensePlate: string;
    carrierName: string;
    status: VehicleStatus;
    plannedArrivalTime?: string;
    currentDockCode?: string;
    currentZoneCode?: string;
}

interface YardSlot {
    id: number;
    dockCode?: string;
    startTime: string;
    endTime: string;
    operationType: OperationType;
    status: SlotStatus;
    vehiclePlate?: string;
}

// ---------------------------------------------------------------------------
// Dados mock – APENAS para a primeira versão de UI.
// Quando tivermos a API /api/yms/..., trocamos por chamadas fetch/axios.
// ---------------------------------------------------------------------------

const MOCK_DOCKS: YardDock[] = [
    {
        id: 1,
        warehouseCode: "WH01",
        code: "D01",
        kind: "INBOUND",
        status: "IN_OPERATION",
    },
    {
        id: 2,
        warehouseCode: "WH01",
        code: "D02",
        kind: "OUTBOUND",
        status: "FREE",
    },
    {
        id: 3,
        warehouseCode: "WH01",
        code: "D03",
        kind: "MIXED",
        status: "RESERVED",
    },
];

const MOCK_VEHICLES: YardVehicle[] = [
    {
        id: 1,
        licensePlate: "AA-00-BB",
        carrierName: "Transportes Norte",
        status: "AT_DOCK",
        plannedArrivalTime: "2025-11-21T13:00:00Z",
        currentDockCode: "D01",
    },
    {
        id: 2,
        licensePlate: "11-CC-22",
        carrierName: "Logística Sul",
        status: "WAITING",
        plannedArrivalTime: "2025-11-21T13:30:00Z",
        currentZoneCode: "Z-ESPERA-1",
    },
    {
        id: 3,
        licensePlate: "33-DD-44",
        carrierName: "EuropaCargo",
        status: "PLANNED",
        plannedArrivalTime: "2025-11-21T14:00:00Z",
    },
];

const MOCK_SLOTS: YardSlot[] = [
    {
        id: 1,
        dockCode: "D01",
        startTime: "2025-11-21T13:00:00Z",
        endTime: "2025-11-21T14:00:00Z",
        operationType: "INBOUND",
        status: "IN_EXECUTION",
        vehiclePlate: "AA-00-BB",
    },
    {
        id: 2,
        dockCode: "D02",
        startTime: "2025-11-21T14:00:00Z",
        endTime: "2025-11-21T15:00:00Z",
        operationType: "OUTBOUND",
        status: "CONFIRMED",
        vehiclePlate: "11-CC-22",
    },
];

// ---------------------------------------------------------------------------
// Helpers de apresentação
// ---------------------------------------------------------------------------

function formatDateTime(dt?: string) {
    if (!dt) return "—";
    const date = new Date(dt);
    if (Number.isNaN(date.getTime())) return dt;
    return date.toLocaleString();
}

function translateDockStatus(status: DockStatus): string {
    switch (status) {
        case "FREE":
            return "Livre";
        case "RESERVED":
            return "Reservada";
        case "IN_OPERATION":
            return "Em operação";
        case "BLOCKED":
            return "Bloqueada";
        case "MAINTENANCE":
            return "Manutenção";
        default:
            return status;
    }
}

function translateVehicleStatus(status: VehicleStatus): string {
    switch (status) {
        case "PLANNED":
            return "Planeado";
        case "ARRIVED":
            return "Chegada registada";
        case "CHECKED_IN":
            return "Check-in efectuado";
        case "WAITING":
            return "Em espera";
        case "CALLED_TO_DOCK":
            return "Chamado para doca";
        case "AT_DOCK":
            return "Na doca";
        case "COMPLETED":
            return "Concluído";
        case "CHECKED_OUT":
            return "Check-out efectuado";
        default:
            return status;
    }
}

function translateSlotStatus(status: SlotStatus): string {
    switch (status) {
        case "PLANNED":
            return "Planeado";
        case "CONFIRMED":
            return "Confirmado";
        case "IN_EXECUTION":
            return "Em execução";
        case "COMPLETED":
            return "Concluído";
        case "NO_SHOW":
            return "No-show";
        case "CANCELLED":
            return "Cancelado";
        default:
            return status;
    }
}

function translateOperationType(op: OperationType): string {
    switch (op) {
        case "INBOUND":
            return "Receção";
        case "OUTBOUND":
            return "Expedição";
        case "RETURN":
            return "Devolução";
        case "CROSS_DOCKING":
            return "Cross-docking";
        default:
            return op;
    }
}

// Badge simples usando Tailwind (sem depender de componente externo)
const StatusPill: React.FC<{ label: string }> = ({ label }) => (
    <span className="inline-flex items-center rounded-full border border-slate-600 px-2 py-0.5 text-xs font-medium text-slate-100">
        {label}
    </span>
);

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

const YardDashboard: React.FC = () => {
    // KPIs simples calculados a partir dos dados mock
    const stats = useMemo(() => {
        const vehiclesInYard = MOCK_VEHICLES.filter(
            (v) => v.status !== "PLANNED" && v.status !== "CHECKED_OUT"
        ).length;

        const docksBusy = MOCK_DOCKS.filter(
            (d) => d.status === "IN_OPERATION" || d.status === "RESERVED"
        ).length;

        const slotsToday = MOCK_SLOTS.length;
        const slotsInExecution = MOCK_SLOTS.filter((s) => s.status === "IN_EXECUTION").length;

        return {
            vehiclesInYard,
            docksBusy,
            slotsToday,
            slotsInExecution,
        };
    }, []);

    return (
        <div className="flex flex-col gap-6">
            {/* Cabeçalho da página */}
            <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-50">
                        Yard — Parque &amp; Docas
                    </h1>
                    <p className="mt-1 text-sm text-slate-300">
                        Visão em tempo real (demo) do movimento de viaturas, ocupação de docas e janelas de carga/descarga.
                        Nesta primeira versão os dados são simulados; depois ligamos às APIs /api/yms/...
                    </p>
                </div>
            </header>

            {/* KPIs principais */}
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-slate-300">Viaturas no parque</div>
                        <Truck className="h-5 w-5 text-sky-400" />
                    </div>
                    <div className="mt-3 text-3xl font-semibold text-slate-50">
                        {stats.vehiclesInYard}
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                        Inclui viaturas em espera e na doca.
                    </p>
                </div>

                <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-slate-300">Docas ocupadas</div>
                        <Warehouse className="h-5 w-5 text-emerald-400" />
                    </div>
                    <div className="mt-3 text-3xl font-semibold text-slate-50">
                        {stats.docksBusy}/{MOCK_DOCKS.length}
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                        Em operação ou reservadas para slot activo.
                    </p>
                </div>

                <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-slate-300">Janelas hoje</div>
                        <Clock className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="mt-3 text-3xl font-semibold text-slate-50">
                        {stats.slotsToday}
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                        Total de slots planeados para o período simulado.
                    </p>
                </div>

                <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-slate-300">Slots em execução</div>
                        <Clock className="h-5 w-5 text-lime-400" />
                    </div>
                    <div className="mt-3 text-3xl font-semibold text-slate-50">
                        {stats.slotsInExecution}
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                        Janelas com operação de carga/descarga em curso.
                    </p>
                </div>
            </section>

            {/* Tabelas principais */}
            <section className="grid gap-6 lg:grid-cols-2">
                {/* Docas */}
                <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
                            Docas
                        </h2>
                        <span className="text-xs text-slate-400">
                            {MOCK_DOCKS.length} docas configuradas
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-700 text-xs uppercase text-slate-400">
                                    <th className="px-2 py-1">Armazém</th>
                                    <th className="px-2 py-1">Doca</th>
                                    <th className="px-2 py-1">Tipo</th>
                                    <th className="px-2 py-1">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {MOCK_DOCKS.map((dock) => (
                                    <tr key={dock.id} className="border-b border-slate-800/60">
                                        <td className="px-2 py-1 text-slate-200">{dock.warehouseCode}</td>
                                        <td className="px-2 py-1 font-medium text-slate-50">{dock.code}</td>
                                        <td className="px-2 py-1 text-slate-200">
                                            {dock.kind === "INBOUND"
                                                ? "Inbound"
                                                : dock.kind === "OUTBOUND"
                                                    ? "Outbound"
                                                    : "Misto"}
                                        </td>
                                        <td className="px-2 py-1">
                                            <StatusPill label={translateDockStatus(dock.status)} />
                                        </td>
                                    </tr>
                                ))}
                                {MOCK_DOCKS.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="px-2 py-4 text-center text-sm text-slate-400"
                                        >
                                            Nenhuma doca configurada.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Viaturas */}
                <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
                            Viaturas (últimos movimentos)
                        </h2>
                        <span className="text-xs text-slate-400">
                            {MOCK_VEHICLES.length} registos simulados
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-700 text-xs uppercase text-slate-400">
                                    <th className="px-2 py-1">Matrícula</th>
                                    <th className="px-2 py-1">Transportador</th>
                                    <th className="px-2 py-1">Estado</th>
                                    <th className="px-2 py-1">ETA</th>
                                    <th className="px-2 py-1">Zona/Doca</th>
                                </tr>
                            </thead>
                            <tbody>
                                {MOCK_VEHICLES.map((v) => (
                                    <tr key={v.id} className="border-b border-slate-800/60">
                                        <td className="px-2 py-1 font-medium text-slate-50">
                                            {v.licensePlate}
                                        </td>
                                        <td className="px-2 py-1 text-slate-200">{v.carrierName}</td>
                                        <td className="px-2 py-1">
                                            <StatusPill label={translateVehicleStatus(v.status)} />
                                        </td>
                                        <td className="px-2 py-1 text-slate-200">
                                            {formatDateTime(v.plannedArrivalTime)}
                                        </td>
                                        <td className="px-2 py-1 text-slate-200">
                                            {v.currentDockCode
                                                ? `Doca ${v.currentDockCode}`
                                                : v.currentZoneCode
                                                    ? `Zona ${v.currentZoneCode}`
                                                    : "—"}
                                        </td>
                                    </tr>
                                ))}
                                {MOCK_VEHICLES.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="px-2 py-4 text-center text-sm text-slate-400"
                                        >
                                            Nenhuma viatura registada.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Janelas de doca */}
            <section className="rounded-xl border border-slate-700 bg-slate-900/60 p-4">
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
                        Janelas de doca (slots)
                    </h2>
                    <span className="text-xs text-slate-400">
                        {MOCK_SLOTS.length} janelas simuladas
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-700 text-xs uppercase text-slate-400">
                                <th className="px-2 py-1">Doca</th>
                                <th className="px-2 py-1">Início</th>
                                <th className="px-2 py-1">Fim</th>
                                <th className="px-2 py-1">Operação</th>
                                <th className="px-2 py-1">Estado</th>
                                <th className="px-2 py-1">Matrícula</th>
                            </tr>
                        </thead>
                        <tbody>
                            {MOCK_SLOTS.map((s) => (
                                <tr key={s.id} className="border-b border-slate-800/60">
                                    <td className="px-2 py-1 text-slate-200">{s.dockCode ?? "—"}</td>
                                    <td className="px-2 py-1 text-slate-200">
                                        {formatDateTime(s.startTime)}
                                    </td>
                                    <td className="px-2 py-1 text-slate-200">
                                        {formatDateTime(s.endTime)}
                                    </td>
                                    <td className="px-2 py-1 text-slate-200">
                                        {translateOperationType(s.operationType)}
                                    </td>
                                    <td className="px-2 py-1">
                                        <StatusPill label={translateSlotStatus(s.status)} />
                                    </td>
                                    <td className="px-2 py-1 text-slate-200">
                                        {s.vehiclePlate ?? "—"}
                                    </td>
                                </tr>
                            ))}
                            {MOCK_SLOTS.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-2 py-4 text-center text-sm text-slate-400"
                                    >
                                        Nenhuma janela configurada.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default YardDashboard;
